"use client";

import { useQuery } from "@apollo/client/react";
import Image from "next/image";
import { useEffect } from "react";

import { graphql } from "../../../../../__generated__/gql";
import { LEGAL_DOCUMENTS } from "../../../../../constants";
import { useGtm } from "../../../../../lib/google-tag-manager/gtm";
import { InstructionList } from "../../../../_components/ui/instruction-list/instruction-list";
import { Link } from "../../../../_components/ui/link";
import { SimpleTable } from "../../../../_components/ui/simple-table/simple-table";

import styles from "./page.module.css";

const ViewerQuery = graphql(`
  query GetViewerForTrialRedirect {
    maybeViewer {
      id
    }
  }
`);

export default function Page() {
  const { data } = useQuery(ViewerQuery);
  const gtm = useGtm();

  const applyPageUrl = `/trial-subscription/apply`;

  // ログイン状態に応じてボタンのリンク先を変更
  // ログイン済み → 直接applyページへ
  // 未ログイン → signup経由でapplyページへ
  const proceedUrl = data?.maybeViewer
    ? applyPageUrl
    : `/auth/signup?returnTo=${encodeURIComponent(applyPageUrl)}`;

  // ページ閲覧開始イベント
  useEffect(() => {
    gtm.sendEvent("trial_subscription_start_page_viewed");
  }, [gtm]);

  return (
    <div className="min-h-screen">
      {/* ヒーローセクション - 新デザイン */}
      <div className="relative aspect-375/480 w-full md:aspect-1440/640">
        <div className="absolute inset-0">
          <Image
            src="/i/trial/hero-bg.jpg"
            alt="SANU 2nd Home Trial"
            fill
            className="object-cover"
            priority
          />
        </div>
        <div className="absolute bottom-[20px] left-[20px] text-white md:top-[160px] md:right-[120px] md:bottom-auto md:left-auto">
          <p className="mb-[16px] text-left text-[16px] leading-[1.6] font-bold tracking-[0.64px] [text-shadow:0px_0px_32px_#000000,0px_0px_15px_#000000] md:mb-[28px] md:text-right md:text-[28px] md:tracking-[1.12px]">
            SANU 2nd Home Trial
          </p>
          <p className="text-left text-[20px] leading-[1.4] font-bold tracking-[2.4px] [text-shadow:0px_0px_32px_#000000] md:text-right md:text-[30px] md:tracking-[3.6px]">
            月額29,700円で、
            <br />
            別荘のある暮らしを試そう。
          </p>
        </div>
      </div>

      {/* メインコンテンツ - 2カラムレイアウト（PC）/ 縦並びレイアウト（SP） */}
      <div className="bg-slightbeige w-full px-[20px] py-[40px] md:px-0 md:py-[80px]">
        <div className="mx-auto flex max-w-[1000px] flex-col gap-[20px] md:flex-row md:gap-[20px]">
          {/* 左カラム（PC）: プラン詳細と注意事項 / SP: order-1とorder-5 */}
          <div className="contents md:flex md:w-[480px] md:flex-col md:gap-[20px]">
            {/* 申し込みのご案内（SP: order-1 / PC: 左カラム上部） */}
            <div className="order-1 flex flex-col gap-[28px] bg-white px-[20px] py-[28px] md:order-0 md:p-[40px]">
              {/* ヘッダー */}
              <div className="flex flex-col gap-[12px] pb-[12px] md:pb-[20px]">
                <div className="bg-sorange inline-flex items-center justify-center self-start rounded-[4px] px-[8px] py-[8px]">
                  <p className="text-[14px] leading-[1.8] font-bold tracking-[0.28px] text-white md:text-[16px] md:tracking-[0.32px]">
                    期間限定募集
                  </p>
                </div>
                <h2 className="text-skogecha text-[20px] leading-[1.4] font-bold tracking-[0.4px] md:text-[24px] md:tracking-[0.48px]">
                  SANU 2nd Home Trial
                  <br />
                  申し込みのご案内
                </h2>
              </div>

              {/* トライアルプラン */}
              <p className="text-skogecha mt-[-8px] mb-[-8px] text-[16px] leading-[1.8] font-bold tracking-[0.64px] md:mt-[-12px] md:mb-[-12px] md:text-[18px] md:leading-[1.4] md:tracking-[0.72px]">
                トライアルプラン
              </p>

              {/* プラン詳細 */}
              <SimpleTable
                data={[
                  {
                    label: "月額基本料金",
                    value: (
                      <span className="text-skogecha">
                        <span className="text-[32px] leading-[1.8] font-bold tracking-[0.56px]">
                          29,700
                        </span>
                        <span className="text-[16px] leading-[1.8] font-bold tracking-[0.56px]">
                          円(税込)
                        </span>
                      </span>
                    ),
                  },
                  {
                    label: "支払日・回数",
                    value: "毎月1日・計6回",
                  },
                  {
                    label: "契約期間",
                    value: "6ヶ月（最長1年まで）",
                  },
                  {
                    label: "宿泊日数",
                    value: "4泊",
                  },
                ]}
                fontSize={12}
                labelEnhanced={true}
                withoutSeparator={true}
                className="text-skogecha [&_td]:align-middle [&_td]:text-[14px] [&_td]:font-medium [&_th]:align-middle [&_th]:tracking-[0.28px] md:[&_th]:text-[14px]"
              />

              {/* 区切り線 */}
              <div className="bg-sbeige my-[-8px] h-px w-full"></div>

              {/* ご利用の流れ */}
              <p className="text-skogecha text-[16px] leading-[1.4] font-bold tracking-[0.64px] md:text-[18px] md:tracking-[0.72px]">
                ご利用の流れ
              </p>

              {/* タイムライン */}
              <div className="flex flex-col">
                {/* 本日申し込み */}
                <div className="relative pb-[20px] pl-[25px]">
                  <div className="absolute top-[5px] left-0 h-[15px] w-[15px]">
                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                      <circle
                        cx="7.5"
                        cy="7.5"
                        r="6"
                        fill="#FA4616"
                        stroke="#FA4616"
                        strokeWidth="3"
                      />
                    </svg>
                  </div>
                  <div className="bg-sorange absolute top-[15px] bottom-[-5px] left-[6px] w-[3px]"></div>
                  <div className="flex items-center gap-[8px]">
                    <p className="text-skogecha text-[14px] leading-[1.4] font-bold tracking-[0.28px] md:text-[16px] md:tracking-[0.32px]">
                      本日申し込み
                    </p>
                    <p className="text-skogecha text-[14px] leading-[1.4] font-bold tracking-[0.28px] md:text-[16px] md:tracking-[0.32px]">
                      予約開始！
                    </p>
                  </div>
                </div>

                {/* 利用開始 */}
                <div className="relative pb-[20px] pl-[25px]">
                  <div className="absolute top-[5px] left-0 h-[15px] w-[15px]">
                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                      <circle
                        cx="7.5"
                        cy="7.5"
                        r="6"
                        fill="#FA4616"
                        stroke="#FA4616"
                        strokeWidth="3"
                      />
                    </svg>
                  </div>
                  <div className="bg-sorange absolute top-[15px] bottom-[-5px] left-[6px] w-[3px]"></div>
                  <p className="text-skogecha text-[14px] leading-[1.4] font-bold tracking-[0.28px] md:text-[16px] md:tracking-[0.32px]">
                    利用開始（開始月1日）
                  </p>
                </div>

                {/* 4泊宿泊 */}
                <div className="relative pb-[20px] pl-[25px]">
                  <div className="bg-sorange absolute top-[-5px] bottom-[-20px] left-[6px] w-[3px]"></div>
                  <div className="bg-slightbeige flex items-center justify-between rounded-[4px] px-[50px] py-[12px] md:px-[60px] md:py-[18px]">
                    <p className="text-skogecha">
                      <span className="text-[28px] leading-[1.8] font-bold tracking-[0.32px]">
                        4
                      </span>
                      <span className="text-[14px] leading-[1.8] font-bold tracking-[0.44px] md:text-[16px] md:tracking-[0.32px]">
                        泊 宿泊
                      </span>
                    </p>
                    <div className="grid grid-cols-2 gap-x-[8px] gap-y-[8px] md:flex md:gap-[8px]">
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-[24px] w-[26px]">
                          <Image
                            src="/i/icon_architecture.svg"
                            alt=""
                            width={26}
                            height={24}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 6ヶ月目 */}
                <div className="relative pb-[20px] pl-[25px]">
                  <div className="absolute top-[10px] left-0 h-[15px] w-[15px]">
                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                      <circle
                        cx="7.5"
                        cy="7.5"
                        r="6"
                        fill="#FA4616"
                        stroke="#FA4616"
                        strokeWidth="3"
                      />
                    </svg>
                  </div>
                  <div className="bg-sorange absolute top-[25px] bottom-[-5px] left-[6px] w-[3px]"></div>
                  <div className="flex items-center gap-[8px]">
                    <p className="text-skogecha text-[14px] leading-[1.4] font-bold tracking-[0.28px] md:text-[16px] md:tracking-[0.32px]">
                      6ヶ月目
                    </p>
                    <div className="bg-skogecha inline-flex items-center justify-center gap-[10px] rounded-[4px] p-[8px]">
                      <p
                        className={`text-[14px] leading-[1.8] font-bold tracking-[0.28px] text-white ${styles["leadingCrop18"]} md:text-[16px] md:tracking-[0.32px]`}
                      >
                        自動継続
                      </p>
                    </div>
                    <p className="text-skogecha text-[14px] leading-[1.4] font-bold tracking-[0.28px] md:text-[16px] md:tracking-[0.32px]">
                      or 解約
                    </p>
                  </div>
                </div>

                {/* 4泊宿泊 */}
                <div className="relative pb-[20px] pl-[25px]">
                  <div className="bg-sorange absolute top-[-5px] bottom-[-5px] left-[6px] w-[3px]"></div>
                  <div className="bg-slightbeige flex items-center justify-between rounded-[4px] px-[50px] py-[12px] md:px-[60px] md:py-[18px]">
                    <p className="text-skogecha">
                      <span className="text-[28px] leading-[1.8] font-bold tracking-[0.32px]">
                        4
                      </span>
                      <span className="text-[14px] leading-[1.8] font-bold tracking-[0.44px] md:text-[16px] md:tracking-[0.32px]">
                        泊 宿泊
                      </span>
                    </p>
                    <div className="grid grid-cols-2 gap-x-[8px] gap-y-[8px] md:flex md:gap-[8px]">
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-[24px] w-[26px]">
                          <Image
                            src="/i/icon_architecture.svg"
                            alt=""
                            width={26}
                            height={24}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 1年 トライアル終了 */}
                <div className="relative pl-[25px]">
                  <div className="absolute top-[5px] left-0 h-[15px] w-[15px]">
                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                      <circle
                        cx="7.5"
                        cy="7.5"
                        r="6"
                        fill="#FA4616"
                        stroke="#FA4616"
                        strokeWidth="3"
                      />
                    </svg>
                  </div>
                  <div className="flex items-center gap-[8px]">
                    <p className="text-skogecha text-[14px] leading-[1.4] font-bold tracking-[0.28px] md:text-[16px] md:tracking-[0.32px]">
                      1年
                    </p>
                    <p className="text-skogecha text-[14px] leading-[1.4] font-bold tracking-[0.28px] md:text-[16px] md:tracking-[0.32px]">
                      トライアル終了
                    </p>
                  </div>
                </div>

                {/* スペーサー */}
                <div className="h-[20px]"></div>

                {/* Co-Ownersの購入で */}
                <div className="bg-slightbeige flex flex-col gap-[12px] rounded-[4px] px-[16px] py-[20px] md:gap-[16px]">
                  <p
                    // eslint-disable-next-line better-tailwindcss/no-unknown-classes
                    className={`text-skogecha text-[14px] leading-[1.8] font-bold tracking-[0.28px] ${styles["leadingCrop18"]} md:text-[16px] md:leading-[1.4] md:tracking-[0.32px] md:${styles["leadingCrop14"]}`}
                  >
                    Co-Ownersの購入で
                  </p>
                  <p
                    // eslint-disable-next-line better-tailwindcss/no-unknown-classes
                    className={`text-skogecha text-[20px] leading-[1.8] font-bold tracking-[-1.2px] ${styles["leadingCrop18"]} md:leading-[1.4] md:tracking-[0.2px] md:${styles["leadingCrop14"]}`}
                  >
                    トライアル期間が実質無料に！
                  </p>
                  <div className="bg-sbeige h-px w-full" />
                  <div className="text-skogecha flex items-start gap-[5px] text-[14px] md:text-[16px]">
                    <p
                      // eslint-disable-next-line better-tailwindcss/no-unknown-classes
                      className={`leading-[1.8] font-normal tracking-[0.28px] ${styles["leadingCrop18"]} md:leading-[1.4] md:tracking-[0.32px] md:${styles["leadingCrop14"]}`}
                    >
                      ・
                    </p>
                    <p
                      // eslint-disable-next-line better-tailwindcss/no-unknown-classes
                      className={`flex-1 leading-[1.8] font-medium tracking-[0.56px] ${styles["leadingCrop18"]} md:leading-[1.6] md:tracking-[0.64px] md:${styles["leadingCrop16"]}`}
                    >
                      トライアル期間中、いつでも共同所有型の別荘{" "}
                      <Link
                        data-analytics-id="trial_start_clicked_co_owners"
                        href="/co-owners/entry"
                        className="font-bold underline decoration-solid"
                        onClick={() =>
                          gtm.sendEvent(
                            "trial_subscription_co_owners_link_clicked",
                          )
                        }
                      >
                        Co-Owners
                      </Link>{" "}
                      の購入ができます
                    </p>
                  </div>
                  <div className="text-skogecha flex items-start gap-[5px] text-[14px] md:text-[16px]">
                    <p
                      // eslint-disable-next-line better-tailwindcss/no-unknown-classes
                      className={`leading-[1.8] font-normal tracking-[0.28px] ${styles["leadingCrop18"]} md:leading-[1.4] md:tracking-[0.32px] md:${styles["leadingCrop14"]}`}
                    >
                      ・
                    </p>
                    <p
                      // eslint-disable-next-line better-tailwindcss/no-unknown-classes
                      className={`flex-1 leading-[1.8] font-medium tracking-[0.56px] ${styles["leadingCrop18"]} md:leading-[1.6] md:tracking-[0.64px] md:${styles["leadingCrop16"]}`}
                    >
                      トライアルで支払った月会費が、
                      <span className="font-bold">
                        そのまま購入代金にあてられます
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 注意事項（SP: order-5 / PC: 左カラム下部） */}
            <div className="order-5 flex flex-1 flex-col gap-[24px] bg-white px-[20px] py-[28px] md:order-0 md:gap-[28px] md:p-[40px]">
              <p
                className={`text-skogecha text-[16px] leading-[1.8] font-bold tracking-[0.64px] ${styles["leadingCrop18"]} md:text-[18px] md:leading-[1.4] md:tracking-[0.72px]`}
              >
                注意事項
              </p>
              <div className="flex flex-col gap-[24px] md:gap-[28px]">
                <div className="text-skogecha flex items-start gap-[5px] text-[12px] leading-[1.8] md:text-[14px] md:leading-[1.4]">
                  <p
                    className={`font-normal tracking-[0.24px] ${styles["leadingCrop18"]} md:tracking-[0.28px]`}
                  >
                    ・
                  </p>
                  <p
                    className={`flex-1 font-medium tracking-[0.48px] ${styles["leadingCrop18"]} md:tracking-[0.56px]`}
                  >
                    契約期間は6ヶ月おきの自動更新、最長1年間です。Co-Ownersを購入する場合を除き、途中解約はできません。
                  </p>
                </div>
                <div className="text-skogecha flex items-start gap-[5px] text-[12px] leading-[1.8] md:text-[14px] md:leading-[1.4]">
                  <p
                    className={`font-normal tracking-[0.24px] ${styles["leadingCrop18"]} md:tracking-[0.28px]`}
                  >
                    ・
                  </p>
                  <p
                    className={`flex-1 font-medium tracking-[0.48px] ${styles["leadingCrop18"]} md:tracking-[0.56px]`}
                  >
                    開始月の途中で申し込んだ場合でも、利用可能な期間は開始月の1日から6ヶ月となります。また、初月の月会費の按分計算はされません。
                  </p>
                </div>
                <div className="text-skogecha flex items-start gap-[5px] text-[12px] leading-[1.8] md:text-[14px] md:leading-[1.4]">
                  <p
                    className={`font-normal tracking-[0.24px] ${styles["leadingCrop18"]} md:tracking-[0.28px]`}
                  >
                    ・
                  </p>
                  <p
                    className={`flex-1 font-medium tracking-[0.48px] ${styles["leadingCrop18"]} md:tracking-[0.56px]`}
                  >
                    2026年1月14日までの期間限定の募集です。
                  </p>
                </div>
                <div className="text-skogecha flex items-start gap-[5px] text-[12px] leading-[1.8] md:text-[14px] md:leading-[1.4]">
                  <p
                    className={`font-normal tracking-[0.24px] ${styles["leadingCrop18"]} md:tracking-[0.28px]`}
                  >
                    ・
                  </p>
                  <p
                    className={`flex-1 font-medium tracking-[0.48px] ${styles["leadingCrop18"]} md:tracking-[0.56px]`}
                  >
                    規定人数に達し次第、または運営判断で早期終了する場合があります。
                  </p>
                </div>
                <div className="text-skogecha flex items-start gap-[5px] text-[12px] leading-[1.8] md:text-[14px] md:leading-[1.4]">
                  <p
                    className={`font-normal tracking-[0.24px] ${styles["leadingCrop18"]} md:tracking-[0.28px]`}
                  >
                    ・
                  </p>
                  <p
                    className={`flex-1 font-medium tracking-[0.48px] ${styles["leadingCrop18"]} md:tracking-[0.56px]`}
                  >
                    トライアルプランで支払った月会費をCo-Owners購入へあてられます。有効期限は契約期間の終了1ヶ月後までです。
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 右カラム（PC）: 申し込みエリア・予約ルール・料金 / SP: order-2〜4 */}
          <div className="contents md:flex md:w-[480px] md:flex-col md:gap-[20px]">
            {/* 申し込みエリア（SP: order-2 / PC: 右カラム上部） */}
            <div className="order-2 flex flex-col gap-[20px] bg-white px-[20px] py-[28px] md:order-0 md:p-[40px]">
              <div className="flex flex-col gap-[16px]">
                <p className="text-skogecha text-[14px] leading-[1.4] font-bold tracking-[0.56px]">
                  2025年12月~2026年2月開始を受付中。
                  <br />
                  「まずはどこに泊まる？」空室状況を確認できます。
                </p>

                {/* 空室状況確認ボタン */}
                <Link
                  data-analytics-id="trial_start_clicked_availability"
                  href="/availability?t=t"
                  target="_blank"
                  onClick={() =>
                    gtm.sendEvent("trial_subscription_availability_clicked")
                  }
                >
                  <div className="bg-sbeige relative flex h-[48px] items-center justify-center gap-[4px] rounded-[50px]">
                    <div className="absolute left-[20px]">
                      <Image
                        src="/i/icon_calendar.svg"
                        alt=""
                        width={18}
                        height={18}
                      />
                    </div>
                    <p className="text-skogecha text-center text-[12px] font-bold tracking-[0.24px] md:text-[14px] md:tracking-[0.28px]">
                      空室状況を確認する
                    </p>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M11 3L3 11M11 3H5M11 3V9"
                        stroke="#332F21"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </Link>

                <p className="text-skogecha text-[12px] leading-[1.4] font-medium tracking-[0.48px] md:text-[14px] md:tracking-[0.56px]">
                  ※リノベーションしたSelection
                  Seriesや、本州以外の遠方拠点は対象外です
                </p>
              </div>

              {/* 区切り線 */}
              <div className="bg-sbeige h-px w-full md:my-[20px]"></div>

              <p className="text-skogecha text-[14px] leading-[1.4] font-bold tracking-[0.28px]">
                下記のプラン詳細を必ずご確認の上、申し込みにお進みください。
              </p>

              {/* プラン申し込みボタン */}
              <a
                data-analytics-id="trial_start_clicked_proceed"
                href={proceedUrl}
                onClick={() =>
                  gtm.sendEvent("trial_subscription_start_proceed_clicked")
                }
              >
                <div className="bg-sorange flex h-[48px] items-center justify-center rounded-[9999px]">
                  <p className="text-center text-[12px] font-bold tracking-[0.24px] text-white md:text-[14px] md:tracking-[0.28px]">
                    プラン申し込みにすすむ
                  </p>
                </div>
              </a>

              <p className="text-skogecha text-[12px] leading-[1.4] font-medium tracking-[0.48px] md:text-[14px] md:tracking-[0.56px]">
                ※ボタンをクリックすることで、
                <Link
                  data-analytics-id="trial_start_clicked_privacy_policy"
                  href={LEGAL_DOCUMENTS.privacyPolicy.url}
                  target="_blank"
                  className="font-bold underline decoration-solid"
                  onClick={() =>
                    gtm.sendEvent("trial_subscription_start_privacy_clicked")
                  }
                >
                  個人情報の取り扱い
                </Link>
                、および
                <Link
                  data-analytics-id="trial_start_clicked_terms"
                  href={LEGAL_DOCUMENTS.termsForTrial.url}
                  target="_blank"
                  className="font-bold underline decoration-solid"
                  onClick={() =>
                    gtm.sendEvent("trial_subscription_start_terms_clicked")
                  }
                >
                  利用規約
                </Link>
                に同意したものとみなします。
              </p>
            </div>

            {/* プラン詳細（SP: order-3 / PC: 右カラム中部） */}
            <div className="order-3 flex flex-col gap-[20px] md:order-0">
              <div className="flex flex-col gap-[24px] bg-white px-[20px] py-[28px] md:gap-[28px] md:p-[40px]">
                <p
                  className={`text-skogecha text-[20px] leading-[1.4] font-bold tracking-[0.4px] ${styles["leadingCrop14"]} md:text-[24px] md:leading-[1.4] md:tracking-[0.48px]`}
                >
                  プラン詳細
                </p>
                <p
                  className={`text-skogecha text-[16px] leading-[1.8] font-bold tracking-[0.64px] ${styles["leadingCrop18"]} md:text-[18px] md:leading-[1.4] md:tracking-[0.72px]`}
                >
                  予約ルール
                </p>
                <InstructionList
                  instructions={[
                    "High日（土・祝・祝前日）の利用は6ヶ月に2泊までです",
                    "同時に2件まで予約をとれます",
                    "3ヶ月先までの予約が可能です（拠点により2ヶ月、1ヶ月前に予約が可能となる場合あります）",
                    "予約泊数の権利は契約期間の6ヶ月を超えて持ち越せません",
                    "SANUが独自で手掛けるOriginal Seriesのお部屋に宿泊可能です（リノベーションしたSelection Seriesや、本州以外の遠方拠点は対象外です）",
                  ]}
                  mark="■"
                  lineSpacing="normal"
                  className={`text-skogecha text-[14px] leading-[1.8] font-medium ${styles["leadingCrop18"]} md:text-[16px] md:leading-[1.8] [&_ul]:md:space-y-4`}
                />
              </div>
            </div>

            {/* 宿泊にかかる料金（SP: order-4 / PC: 右カラム下部） */}
            <div className="order-4 flex flex-1 flex-col gap-[16px] bg-white px-[20px] py-[28px] md:order-0 md:gap-[28px] md:p-[40px]">
              <p
                className={`text-skogecha text-[16px] leading-[1.8] font-bold tracking-[0.64px] ${styles["leadingCrop18"]} md:text-[18px] md:leading-[1.4] md:tracking-[0.72px]`}
              >
                宿泊にかかる料金
              </p>

              <div className="flex flex-col gap-[16px] md:gap-[28px]">
                <div className="border-sbeige border-y py-[8px] md:py-[24px]">
                  <SimpleTable
                    data={[
                      {
                        label: "清掃料金",
                        value: "3,300円/滞在",
                      },
                    ]}
                    fontSize={12}
                    labelEnhanced={true}
                    withoutSeparator={true}
                    className="text-skogecha [&_td]:align-middle [&_td]:font-medium md:[&_td]:text-[14px] [&_th]:align-middle [&_th]:tracking-[0.28px] md:[&_th]:text-[14px]"
                  />
                </div>

                <p
                  className={`text-skogecha text-[14px] leading-[1.8] font-bold tracking-[0.28px] ${styles["leadingCrop18"]} md:text-[16px] md:leading-[1.4] md:tracking-[0.32px]`}
                >
                  オプション
                </p>

                <SimpleTable
                  data={[
                    {
                      label: "サウナ・温泉",
                      value: "5,500円/泊",
                    },
                    {
                      label: "愛犬同伴",
                      value: "3,300円/滞在",
                    },
                    {
                      label: "EV充電器",
                      value: "1,650円/滞在",
                    },
                  ]}
                  fontSize={12}
                  labelEnhanced={true}
                  withoutSeparator={false}
                  className="text-skogecha [&_td]:align-middle [&_td]:font-medium md:[&_td]:text-[14px] [&_th]:align-middle [&_th]:tracking-[0.28px] md:[&_th]:text-[14px]"
                />

                <div className="text-skogecha flex justify-end text-[12px] leading-[1.8] md:text-[14px] md:leading-[1.4]">
                  <span className="font-normal tracking-[0.24px] md:tracking-[0.28px]">
                    ＊
                  </span>
                  <span className="font-medium tracking-[0.48px] md:tracking-[0.56px]">
                    全て税込
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
